import { UiVariable } from 'pages/AutomationEngine/dialogs/VariableWizard/types';

import { TriggerStep } from '../triggerSectionTypes';
import {
  KizenTriggerType,
  VariableDefinitionType,
} from 'pages/AutomationEngine/types';
import { isVariableInitialization } from 'pages/AutomationEngine/dialogs/VariableWizard/utilities';

type ActivityTriggerType = Extract<
  KizenTriggerType,
  'activity_logged' | 'scheduled_activity_overdue'
>;

const matchActivityLoggedActivityTrigger = (
  activityId: string,
  sourceType: VariableDefinitionType,
  valueCategoryValue: string
) =>
  sourceType === 'logged_activity_trigger' && valueCategoryValue === activityId;

const matchActivityPassedDueActivityTrigger = (
  activityId: string,
  sourceType: VariableDefinitionType,
  valueCategoryValue: string
) =>
  sourceType === 'activity_past_due_trigger' &&
  valueCategoryValue === activityId;

// =============>
export const activityInUse = (
  variables: UiVariable[],
  activityId: string,
  triggerType: ActivityTriggerType = 'activity_logged'
) => {
  const inUse = variables.some((variable) => {
    if (isVariableInitialization(variable.stepType, variable.type)) {
      return variable.details.sources.some(
        ({
          sourceType,
          loggedActivityVariable,
          activityPassedDueVariable,
          ...rest
        }) => {
          if (
            triggerType === 'activity_logged' &&
            sourceType === 'logged_activity_trigger'
          ) {
            return matchActivityLoggedActivityTrigger(
              activityId,
              sourceType,
              loggedActivityVariable?.valueCategory?.value!
            );
          } else if (
            triggerType === 'scheduled_activity_overdue' &&
            sourceType === 'activity_past_due_trigger'
          ) {
            return matchActivityPassedDueActivityTrigger(
              activityId,
              sourceType,
              activityPassedDueVariable?.valueCategory?.value!
            );
          }
        }
      );
    }
    return false;
  });
  return inUse;
};

export const activityLoggedInUse = (
  variables: UiVariable[],
  activityId: string
) => activityInUse(variables, activityId, 'activity_logged');

export const activityPassedDueInUse = (
  variables: UiVariable[],
  activityId: string
) => activityInUse(variables, activityId, 'scheduled_activity_overdue');

// =============>
export const activityTriggerCount = (
  triggers: TriggerStep[],
  activityId: string,
  triggerType: ActivityTriggerType = 'activity_logged'
): number => {
  const count = triggers.filter((trigger) => {
    if (trigger.type === triggerType) {
      return trigger.details?.activity?.value === activityId;
    }
    return false;
  });

  return count.length;
};

export const activityLoggedTriggerCount = (
  triggers: TriggerStep[],
  activityId: string
): number => activityTriggerCount(triggers, activityId, 'activity_logged');

export const activityPastDueTriggerCount = (
  triggers: TriggerStep[],
  activityId: string
): number =>
  activityTriggerCount(triggers, activityId, 'scheduled_activity_overdue');

// =============>
export const removeActivities = (
  variables: UiVariable[],
  activityId: string | null | undefined,
  triggerType: ActivityTriggerType = 'activity_logged'
) => {
  const errorIds: string[] = [];

  if (activityId && variables.length) {
    const nextVariables = variables.reduce<UiVariable[]>(
      (previous, variable) => {
        if (isVariableInitialization(variable.stepType, variable.type)) {
          let hasError = variable.hasError || false;

          const nextSources = variable.details.sources.map((source) => {
            const { sourceType } = source;
            if (
              source.sourceType === 'logged_activity_trigger' &&
              typeof source.loggedActivityVariable?.valueCategory ===
                'object' &&
              matchActivityLoggedActivityTrigger(
                activityId,
                sourceType as VariableDefinitionType,
                source.loggedActivityVariable?.valueCategory?.value!
              )
            ) {
              if (!errorIds.includes(variable.id)) {
                hasError = true;
                errorIds.push(variable.id);
              }
              return {
                ...source,
                sourceType,
                loggedActivityVariable: {
                  ...source.loggedActivityVariable,
                  valueCategory: undefined,
                  value: undefined,
                },
              };
            }

            if (
              source.sourceType === 'activity_past_due_trigger' &&
              typeof source.activityPassedDueVariable?.valueCategory ===
                'object' &&
              matchActivityPassedDueActivityTrigger(
                activityId,
                sourceType as VariableDefinitionType,
                source.activityPassedDueVariable?.valueCategory?.value!
              )
            ) {
              if (!errorIds.includes(variable.id)) {
                hasError = true;
                errorIds.push(variable.id);
              }
              return {
                ...source,
                sourceType,
                activityPassedDueVariable: {
                  ...source.activityPassedDueVariable,
                  valueCategory: undefined,
                  value: undefined,
                },
              };
            }

            return source;
          });

          previous.push({
            ...variable,
            hasError,
            details: { ...variable.details, sources: nextSources },
          });
        } else {
          // no match just add is
          previous.push({ ...variable });
        }
        return previous;
      },
      []
    );

    return { variables: nextVariables, errorIds };
  }
  return { variables, errorIds };
};

export const removeActivityLoggedActivities = (
  variables: UiVariable[],
  activityId: string | null | undefined
) => removeActivities(variables, activityId, 'activity_logged');

export const removeActivityPassedDueActivities = (
  variables: UiVariable[],
  activityId: string | null | undefined
) => removeActivities(variables, activityId, 'scheduled_activity_overdue');

export const activityChanged = (prevStep: TriggerStep, step: TriggerStep) =>
  prevStep.details?.activity?.value !== step.details?.activity?.value;
